import api from './api.service';

export type HrmsAgingBucket = '0-7' | '8-14' | '15-30' | '30+';
export type HrmsAgingMap = Record<HrmsAgingBucket, number>;

export type HrmsFollowUpMode =
  | 'expired_docs'
  | 'docs_due_soon'
  | 'contracts_ending'
  | 'pending_leave'
  | 'pending_ot'
  | 'probation_ending'
  | 'confirmation_due'
  | 'onboarding_overdue';

export interface HrmsPreviewRow {
  id: string;
  link_path: string;
  title: string;
  subtitle?: string;
  status?: string;
  employee_name?: string;
  aging_days?: number;
}

export interface HrmsFollowUpRow {
  id: string;
  link_path: string;
  title: string;
  subtitle?: string;
  status?: string;
  employee_name?: string;
  employee_code?: string;
  ref_date?: string;
  aging_days: number;
  aging_bucket: HrmsAgingBucket;
  extra?: string;
}

export interface HrmsFollowUpResult {
  mode: HrmsFollowUpMode;
  total: number;
  page: number;
  pageSize: number;
  aging: HrmsAgingMap;
  rows: HrmsFollowUpRow[];
}

export interface HrmsTalentSnapshot {
  enabled: boolean;
  open_requisitions: number;
  pending_approvals: number;
  published_jobs: number;
  active_pipeline: number;
  interviews_next_7d: number;
  offers_awaiting_response: number;
  onboarding_in_progress: number;
  overdue_onboarding_tasks: number;
  hires_this_month: number;
}

export interface HrmsDashboardActionSummary {
  as_on_date: string;
  features: {
    recruitment: boolean;
    payroll: boolean;
    attendance: boolean;
    eosb: boolean;
  };
  active_headcount: number;
  on_leave_today_count: number;
  joiners_this_week_count: number;
  leavers_this_week_count: number;
  joiners_next_14d_count: number;
  expired_docs_count: number;
  docs_due_soon_count: number;
  docs_due_soon_aging: HrmsAgingMap;
  expired_docs_aging: HrmsAgingMap;
  expired_docs: HrmsPreviewRow[];
  docs_due_soon: HrmsPreviewRow[];
  contracts_ending_count: number;
  contracts_ending: HrmsPreviewRow[];
  pending_leave_count: number;
  pending_leave_aging: HrmsAgingMap;
  pending_leave: HrmsPreviewRow[];
  pending_ot_count: number;
  pending_ot: HrmsPreviewRow[];
  pending_profile_change_count: number;
  pending_profile_changes: HrmsPreviewRow[];
  pending_salary_revision_count: number;
  pending_salary_revisions: HrmsPreviewRow[];
  confirmation_due_count: number;
  confirmation_due: HrmsPreviewRow[];
  probation_ending_count: number;
  probation_ending: HrmsPreviewRow[];
  overdue_loan_installment_count: number;
  open_loan_exposure: number;
  payroll_enabled: boolean;
  payroll_period_id?: string;
  payroll_period_label?: string;
  payroll_period_status?: string;
  unapproved_timesheet_count: number;
  draft_adjustment_count: number;
  talent: HrmsTalentSnapshot;
}

function emptyAging(): HrmsAgingMap {
  return { '0-7': 0, '8-14': 0, '15-30': 0, '30+': 0 };
}

function normalizeAging(raw: unknown): HrmsAgingMap {
  const base = emptyAging();
  if (!raw || typeof raw !== 'object') return base;
  const src = raw as Record<string, number>;
  for (const key of Object.keys(base) as HrmsAgingBucket[]) {
    base[key] = Number(src[key] ?? 0);
  }
  return base;
}

function emptyTalent(): HrmsTalentSnapshot {
  return {
    enabled: false,
    open_requisitions: 0,
    pending_approvals: 0,
    published_jobs: 0,
    active_pipeline: 0,
    interviews_next_7d: 0,
    offers_awaiting_response: 0,
    onboarding_in_progress: 0,
    overdue_onboarding_tasks: 0,
    hires_this_month: 0,
  };
}

export function emptyHrmsDashboardSummary(): HrmsDashboardActionSummary {
  return {
    as_on_date: '',
    features: { recruitment: false, payroll: false, attendance: false, eosb: false },
    active_headcount: 0,
    on_leave_today_count: 0,
    joiners_this_week_count: 0,
    leavers_this_week_count: 0,
    joiners_next_14d_count: 0,
    expired_docs_count: 0,
    docs_due_soon_count: 0,
    docs_due_soon_aging: emptyAging(),
    expired_docs_aging: emptyAging(),
    expired_docs: [],
    docs_due_soon: [],
    contracts_ending_count: 0,
    contracts_ending: [],
    pending_leave_count: 0,
    pending_leave_aging: emptyAging(),
    pending_leave: [],
    pending_ot_count: 0,
    pending_ot: [],
    pending_profile_change_count: 0,
    pending_profile_changes: [],
    pending_salary_revision_count: 0,
    pending_salary_revisions: [],
    confirmation_due_count: 0,
    confirmation_due: [],
    probation_ending_count: 0,
    probation_ending: [],
    overdue_loan_installment_count: 0,
    open_loan_exposure: 0,
    payroll_enabled: false,
    unapproved_timesheet_count: 0,
    draft_adjustment_count: 0,
    talent: emptyTalent(),
  };
}

export async function getHrmsDashboardActionSummary(params?: {
  as_on_date?: string;
}): Promise<HrmsDashboardActionSummary> {
  const res = await api.get<{ data?: HrmsDashboardActionSummary }>('/hr/dashboard/action-summary', {
    params: params?.as_on_date ? { as_on_date: params.as_on_date } : undefined,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  if (!data || typeof data !== 'object') return emptyHrmsDashboardSummary();
  const talentRaw = data.talent || {};
  return {
    ...emptyHrmsDashboardSummary(),
    as_on_date: String(data.as_on_date ?? ''),
    features: {
      recruitment: Boolean(data.features?.recruitment),
      payroll: Boolean(data.features?.payroll),
      attendance: Boolean(data.features?.attendance),
      eosb: Boolean(data.features?.eosb),
    },
    active_headcount: Number(data.active_headcount ?? 0),
    on_leave_today_count: Number(data.on_leave_today_count ?? 0),
    joiners_this_week_count: Number(data.joiners_this_week_count ?? 0),
    leavers_this_week_count: Number(data.leavers_this_week_count ?? 0),
    joiners_next_14d_count: Number(data.joiners_next_14d_count ?? 0),
    expired_docs_count: Number(data.expired_docs_count ?? 0),
    docs_due_soon_count: Number(data.docs_due_soon_count ?? 0),
    expired_docs_aging: normalizeAging(data.expired_docs_aging),
    docs_due_soon_aging: normalizeAging(data.docs_due_soon_aging),
    expired_docs: Array.isArray(data.expired_docs) ? data.expired_docs : [],
    docs_due_soon: Array.isArray(data.docs_due_soon) ? data.docs_due_soon : [],
    contracts_ending_count: Number(data.contracts_ending_count ?? 0),
    contracts_ending: Array.isArray(data.contracts_ending) ? data.contracts_ending : [],
    pending_leave_count: Number(data.pending_leave_count ?? 0),
    pending_leave_aging: normalizeAging(data.pending_leave_aging),
    pending_leave: Array.isArray(data.pending_leave) ? data.pending_leave : [],
    pending_ot_count: Number(data.pending_ot_count ?? 0),
    pending_ot: Array.isArray(data.pending_ot) ? data.pending_ot : [],
    pending_profile_change_count: Number(data.pending_profile_change_count ?? 0),
    pending_profile_changes: Array.isArray(data.pending_profile_changes)
      ? data.pending_profile_changes
      : [],
    pending_salary_revision_count: Number(data.pending_salary_revision_count ?? 0),
    pending_salary_revisions: Array.isArray(data.pending_salary_revisions)
      ? data.pending_salary_revisions
      : [],
    confirmation_due_count: Number(data.confirmation_due_count ?? 0),
    confirmation_due: Array.isArray(data.confirmation_due) ? data.confirmation_due : [],
    probation_ending_count: Number(data.probation_ending_count ?? 0),
    probation_ending: Array.isArray(data.probation_ending) ? data.probation_ending : [],
    overdue_loan_installment_count: Number(data.overdue_loan_installment_count ?? 0),
    open_loan_exposure: Number(data.open_loan_exposure ?? 0),
    payroll_enabled: Boolean(data.payroll_enabled),
    payroll_period_id: data.payroll_period_id,
    payroll_period_label: data.payroll_period_label,
    payroll_period_status: data.payroll_period_status,
    unapproved_timesheet_count: Number(data.unapproved_timesheet_count ?? 0),
    draft_adjustment_count: Number(data.draft_adjustment_count ?? 0),
    talent: {
      enabled: Boolean(talentRaw.enabled),
      open_requisitions: Number(talentRaw.open_requisitions ?? 0),
      pending_approvals: Number(talentRaw.pending_approvals ?? 0),
      published_jobs: Number(talentRaw.published_jobs ?? 0),
      active_pipeline: Number(talentRaw.active_pipeline ?? 0),
      interviews_next_7d: Number(talentRaw.interviews_next_7d ?? 0),
      offers_awaiting_response: Number(talentRaw.offers_awaiting_response ?? 0),
      onboarding_in_progress: Number(talentRaw.onboarding_in_progress ?? 0),
      overdue_onboarding_tasks: Number(talentRaw.overdue_onboarding_tasks ?? 0),
      hires_this_month: Number(talentRaw.hires_this_month ?? 0),
    },
  };
}

const QUEUE_MODES: HrmsFollowUpMode[] = [
  'expired_docs',
  'docs_due_soon',
  'contracts_ending',
  'pending_leave',
  'pending_ot',
  'probation_ending',
  'confirmation_due',
  'onboarding_overdue',
];

export async function getHrmsFollowUpQueue(params?: {
  mode?: HrmsFollowUpMode;
  page?: number;
  pageSize?: number;
  aging_bucket?: HrmsAgingBucket;
  search?: string;
  as_on_date?: string;
}): Promise<HrmsFollowUpResult> {
  const res = await api.get<{ data?: HrmsFollowUpResult }>('/hr/dashboard/follow-up-queue', {
    params,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  return {
    mode: (QUEUE_MODES.includes(data?.mode) ? data.mode : 'expired_docs') as HrmsFollowUpMode,
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? 1),
    pageSize: Number(data?.pageSize ?? 20),
    aging: normalizeAging(data?.aging),
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}
